// The game being played: one H1 simulation on its clock, fed real time whether or not anything is
// drawn, so the factory keeps working while the player looks at the map (PRD section 10). Views read
// its report; only commands change it.
import { buildReport, type Report } from './sim/report';
import { Simulation } from './sim/simulation';
import { loadGameData, loadStartScenario } from './sim/game-data';
import { TICK_MS } from './sim/flow';

/** How often the clock is fed when no frame is drawn, in milliseconds (below the clock's 250 ms cut). */
export const PUMP_MS = 100;

export class Game {
  readonly sim: Simulation;
  private last: number | undefined;
  private cached: Report | undefined;
  private cachedKey = '';
  /** Frozen (screenshots): real time never moves the clock; only `advance` does. */
  readonly frozen: boolean;

  constructor(opts: { readonly frozen?: boolean } = {}) {
    this.sim = Simulation.withScenario(loadGameData(), loadStartScenario());
    this.frozen = opts.frozen ?? false;
  }

  get data(): Simulation['data'] {
    return this.sim.data;
  }

  /**
   * Feeds the clock the real time since the last call; returns the ticks it ran. Called on every
   * drawn frame and on a timer, so it never matters which of them comes first.
   */
  pump(now: number): number {
    const last = this.last;
    this.last = now;
    if (this.frozen || last === undefined) return 0;
    return this.sim.frame(now - last);
  }

  /** The app was hidden: the next call starts afresh, so the factory stops while away (D10). */
  rest(): void {
    this.last = undefined;
  }

  /** Runs game time straight away (tests and screenshots). */
  advance(gameMs: number): void {
    this.sim.runFor(gameMs);
  }

  /** Game minutes for the items on the belts, between ticks included, so they glide instead of stepping. */
  get visualMinutes(): number {
    const pending = this.frozen || this.sim.clock.paused ? 0 : this.sim.clock.pendingMs;
    return (this.sim.time + Math.min(pending, TICK_MS)) / 60_000;
  }

  /** The figures as of now; built once per tick, layout change or clock change. */
  report(): Report {
    const sim = this.sim;
    const key = `${sim.world.tick}:${sim.world.layoutVersion}:${sim.clock.speed}:${sim.clock.paused}:${sim.history.undoLabel ?? ''}:${sim.history.redoLabel ?? ''}`;
    if (!this.cached || key !== this.cachedKey) {
      this.cached = buildReport(sim);
      this.cachedKey = key;
    }
    return this.cached;
  }
}
